/* eslint-disable */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdClose,
  MdDarkMode,
  MdLightMode,
  MdLock,
  MdLogout,
  MdMenu,
  MdPerson,
  MdSearch,
  MdWarning,
} from "react-icons/md";
import BrandLogo from "components/brand/BrandLogo";
import Links from "./components/Links";
import Dropdown from "components/dropdown";
import { useLanguage } from "context/LanguageContext";
import { authService, UserMe } from "services/authService";

import routes from "routes";
import { useTheme } from "context/ThemeContext";
import { isRenderableAvatar } from "utils/avatar";

const KNOWN_ROLES = [
  "SYSTEM_ADMIN",
  "MANAGER",
  "TEACHER",
  "TRAINER",
  "STUDENT",
];

// Translated templates mark the center name / day count with {placeholders};
// split on them so those values keep their <strong> emphasis.
const renderWithBold = (
  template: string,
  values: Record<string, React.ReactNode>
) =>
  template.split(/(\{\w+\})/).map((part, index) => {
    const name = part.match(/^\{(\w+)\}$/)?.[1];
    return name && name in values ? (
      <strong key={index}>{values[name]}</strong>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    );
  });

const Sidebar = (props: {
  open: boolean;
  collapsed: boolean;
  onClose: React.MouseEventHandler<HTMLSpanElement>;
  onToggleCollapse: () => void;
  routes?: RoutesType[];
}) => {
  const {
    open,
    collapsed,
    onClose,
    onToggleCollapse,
    routes: propRoutes,
  } = props;
  const activeRoutes = propRoutes ?? routes;
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [currentUser, setCurrentUser] = useState<UserMe | null>(() =>
    authService.getStoredUser()
  );
  const avatarSrc = isRenderableAvatar(currentUser?.avatar_url)
    ? currentUser?.avatar_url
    : null;
  const displayName =
    currentUser?.full_name?.trim() || t("sidebar.defaultUserName");
  const roleLabel = currentUser?.role
    ? KNOWN_ROLES.includes(currentUser.role)
      ? t(`enum.role.${currentUser.role}`)
      : currentUser.role
    : "";
  const sidebarRoutes: RoutesType[] = Array.isArray(activeRoutes)
    ? (activeRoutes as RoutesType[])
    : [];
  const profileRoute = sidebarRoutes.find(
    (route: RoutesType) => route.name === "nav.settings"
  );
  const profilePath = profileRoute
    ? `${profileRoute.layout}/${profileRoute.path.replace(/\/\*$/, "")}`
    : null;
  const changePasswordRoute = sidebarRoutes.find(
    (route: RoutesType) => route.name === "nav.changePassword"
  );
  const changePasswordPath = changePasswordRoute
    ? `${changePasswordRoute.layout}/${changePasswordRoute.path.replace(
        /\/\*$/,
        ""
      )}`
    : null;

  useEffect(() => {
    authService
      .getMe()
      .then(setCurrentUser)
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await authService.logout();
    navigate("/auth/sign-in", { replace: true });
  };
  // Trial expiration logic
  const trialInfo = React.useMemo(() => {
    if (!currentUser?.trial_end_date) return null;
    if (currentUser.role === "SYSTEM_ADMIN") return null;
    // Use UTC dates to match backend _compute_days_remaining() exactly
    const end = new Date(currentUser.trial_end_date);
    const endDateUTC = Date.UTC(
      end.getUTCFullYear(),
      end.getUTCMonth(),
      end.getUTCDate()
    );
    const now = new Date();
    const todayUTC = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );
    const diffDays = Math.round(
      (endDateUTC - todayUTC) / (1000 * 60 * 60 * 24)
    );
    if (diffDays > 30) return null;
    return { diffDays, expired: diffDays < 0 };
  }, [currentUser?.trial_end_date, currentUser?.role]);

  const [showTrialPopup, setShowTrialPopup] = useState(false);

  useEffect(() => {
    if (!trialInfo) return;
    // Show popup once per session
    const key = `trial_popup_shown_${currentUser?.center_id ?? ""}`;
    if (!sessionStorage.getItem(key)) {
      setShowTrialPopup(true);
      sessionStorage.setItem(key, "1");
    }
  }, [trialInfo, currentUser?.center_id]);

  const { isDark, toggleTheme } = useTheme();
  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="bg-black/50 fixed inset-0 z-40 xl:hidden"
          onClick={onClose as any}
        />
      )}
      <div
        className={`fixed !z-50 flex h-full flex-col border-r border-line bg-surface shadow-2xl shadow-shadow-500 transition-all duration-300 ease-in-out dark:shadow-none ${open ? "translate-x-0" : "-translate-x-full"}
          xl:translate-x-0
          ${collapsed ? "w-[80px]" : "w-[280px]"}
        `}
      >
        {/* Mobile close button */}
        <span
          className="absolute right-4 top-4 block cursor-pointer xl:hidden"
          onClick={onClose}
        >
          <MdClose className="h-5 w-5" />
        </span>

        {/* Header: logo-only when collapsed, logo + toggle when expanded */}
        <div
          className={`mb-2 mt-6 flex items-center ${
            collapsed ? "justify-center px-2" : "justify-between px-6"
          }`}
        >
          <button
            type="button"
            onClick={collapsed ? onToggleCollapse : undefined}
            className={`flex items-center ${
              collapsed ? "cursor-pointer" : "cursor-default"
            }`}
            aria-label={collapsed ? t("sidebar.expand") : t("sidebar.logoAlt")}
          >
            <BrandLogo
              size={collapsed ? "sm" : "md"}
              showWordmark={!collapsed}
            />
          </button>
          {!collapsed ? (
            <button
              onClick={onToggleCollapse}
              className="hidden h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink xl:flex"
              aria-label={t("sidebar.collapse")}
            >
              <MdMenu className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div
          className={`mb-3 mt-4 h-px bg-line ${
            collapsed ? "mx-3" : "mx-6"
          }`}
        />

        {/* Search + Dark mode + Language — single row */}
        <div
          className={`mb-3 flex items-center gap-1.5 ${
            collapsed ? "flex-col px-2" : "px-4"
          }`}
        >
          {/* Search */}
          {!collapsed ? (
            <div className="flex flex-1 items-center gap-2 rounded-full bg-surface-muted px-3 py-2">
              <MdSearch className="h-4 w-4 shrink-0 text-ink-subtle" />
              <input
                type="text"
                placeholder={t("navbar.search")}
                className="bg-transparent w-full text-sm text-ink outline-none placeholder:text-ink-subtle"
              />
            </div>
          ) : (
            <button
              className="flex h-9 w-9 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
              title={t("navbar.search")}
            >
              <MdSearch className="h-4 w-4" />
            </button>
          )}
          {/* Dark mode */}
          <button
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            onClick={toggleTheme}
            aria-label={t(isDark ? "theme.switchToLight" : "theme.switchToDark")}
          >
            {isDark ? (
              <MdLightMode className="h-4 w-4" />
            ) : (
              <MdDarkMode className="h-4 w-4" />
            )}
          </button>
          {/* Language */}
          <div
            className={`flex shrink-0 items-center gap-0.5 rounded-full bg-surface-muted px-1 py-0.5 ${
              collapsed ? "flex-col" : ""
            }`}
          >
            <button
              onClick={() => setLanguage("vi")}
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold transition-all ${
                language === "vi"
                  ? "bg-brand-500 text-white shadow"
                  : "text-ink-muted hover:text-brand-500"
              }`}
            >
              VI
            </button>
            <button
              onClick={() => setLanguage("en")}
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold transition-all ${
                language === "en"
                  ? "bg-brand-500 text-white shadow"
                  : "text-ink-muted hover:text-brand-500"
              }`}
            >
              EN
            </button>
          </div>
        </div>

        <div
          className={`mb-2 h-px bg-line ${
            collapsed ? "mx-3" : "mx-6"
          }`}
        />

        {/* Nav items */}
        <ul className="mb-auto overflow-y-auto overflow-x-hidden pt-1">
          <Links routes={activeRoutes} collapsed={collapsed} />
        </ul>

        {/* Trial expiration banner */}
        {trialInfo && !collapsed && (
          <div
            className={`mx-3 mb-2 cursor-pointer rounded-lg px-3 py-2 text-xs font-medium ${
              trialInfo.expired
                ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                : trialInfo.diffDays <= 7
                ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
            }`}
            onClick={() => setShowTrialPopup(true)}
          >
            <div className="flex items-center gap-1.5">
              <MdWarning className="h-4 w-4 shrink-0" />
              <span>
                {trialInfo.expired
                  ? t("sidebar.trialExpiredShort")
                  : t("sidebar.trialDaysLeft", { days: trialInfo.diffDays })}
              </span>
            </div>
          </div>
        )}
        {trialInfo && collapsed && (
          <div className="mb-2 flex justify-center">
            <button
              onClick={() => setShowTrialPopup(true)}
              className={`flex h-9 w-9 items-center justify-center rounded-full ${
                trialInfo.expired
                  ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                  : trialInfo.diffDays <= 7
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                  : "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
              }`}
              title={
                trialInfo.expired
                  ? t("sidebar.trialExpiredShort")
                  : t("sidebar.trialDaysLeft", { days: trialInfo.diffDays })
              }
            >
              <MdWarning className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Bottom user section */}
        <div
          className={`border-t border-line p-4 ${ collapsed ? "flex justify-center" : ""
          }`}
        >
          <Dropdown
            button={
              collapsed ? (
                avatarSrc ? (
                  <img
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-full object-cover"
                    src={avatarSrc}
                    alt={t("sidebar.avatarAlt")}
                  />
                ) : (
                  <div className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-line text-ink-muted">
                    <MdPerson className="h-5 w-5" />
                  </div>
                )
              ) : (
                <div className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-1 transition-colors hover:bg-surface-hover">
                  {avatarSrc ? (
                    <img
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                      src={avatarSrc}
                      alt={t("sidebar.avatarAlt")}
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-line text-ink-muted">
                      <MdPerson className="h-5 w-5" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-start gap-2">
                      <p className="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-ink">
                        {currentUser?.full_name ?? "..."}
                      </p>
                      {roleLabel ? (
                        <span className="mt-0.5 inline-flex w-fit shrink-0 whitespace-nowrap rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                          {roleLabel}
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-ink-muted">
                      {currentUser?.email ?? ""}
                    </p>
                    {currentUser?.center_name && (
                      <p className="mt-0.5 truncate text-[10px] text-brand-600">
                        {currentUser.center_name}
                      </p>
                    )}
                  </div>
                </div>
              )
            }
            classNames={`py-2 ${
              collapsed ? "bottom-full left-0" : "bottom-full left-0"
            } w-max`}
            animation="origin-bottom-left transition-all duration-200 ease-in-out"
            children={
              <div className="flex w-64 flex-col rounded-[16px] border border-line bg-surface p-4 shadow-xl shadow-shadow-500 dark:text-navy-700 dark:shadow-none">
                <div className="mb-2 px-1">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 break-words text-sm font-bold leading-5 text-ink">
                      {t("navbar.greeting")}, {displayName}
                    </p>
                    {roleLabel ? (
                      <span className="mt-0.5 inline-flex w-fit shrink-0 whitespace-nowrap rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                        {roleLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="h-px w-full bg-line" />
                {profilePath ? (
                  <a
                    href={profilePath}
                    className="mt-2 flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-hover"
                  >
                    <MdPerson className="h-4 w-4" />
                    {t("navbar.profileSettings")}
                  </a>
                ) : null}
                {changePasswordPath ? (
                  <a
                    href={changePasswordPath}
                    className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-hover"
                  >
                    <MdLock className="h-4 w-4" />
                    {t("nav.changePassword")}
                  </a>
                ) : null}
                <div className="mt-1 h-px w-full bg-line" />
                <button
                  onClick={handleLogout}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
                >
                  <MdLogout className="h-4 w-4" />
                  {t("navbar.logout")}
                </button>
              </div>
            }
          />
        </div>
      </div>

      {/* Trial expiration popup */}
      {showTrialPopup && trialInfo && (
        <div
          className="bg-black/50 fixed inset-0 z-[100] flex items-center justify-center px-4"
          onClick={(e) =>
            e.target === e.currentTarget && setShowTrialPopup(false)
          }
        >
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                  trialInfo.expired ? "bg-red-100 dark:bg-red-500/15" : "bg-amber-100 dark:bg-amber-500/15"
                }`}
              >
                <MdWarning
                  className={`h-6 w-6 ${
                    trialInfo.expired ? "text-red-600 dark:text-red-300" : "text-amber-600 dark:text-amber-300"
                  }`}
                />
              </div>
              <h3 className="text-lg font-bold text-ink">
                {trialInfo.expired
                  ? t("sidebar.trialExpiredTitle")
                  : t("sidebar.trialExpiringTitle")}
              </h3>
            </div>
            <p className="text-sm text-ink">
              {trialInfo.expired
                ? renderWithBold(t("sidebar.trialExpiredBody"), {
                    center: currentUser?.center_name,
                  })
                : renderWithBold(t("sidebar.trialExpiringBody"), {
                    center: currentUser?.center_name,
                    duration: t("sidebar.trialDays", {
                      days: trialInfo.diffDays,
                    }),
                  })}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTrialPopup(false)}
                className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                {t("sidebar.gotIt")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
